import { BannerMode } from '../../display/ui2-banner/ui2-banner-props'
import { UI2GeolocationFieldProps } from './ui2-geolocation-field-props'

/**
 * `denied` and `unavailable` are kept apart because only one of them is
 * recoverable by the user: a denied permission has a settings screen (native)
 * or a browser setting (web) behind it, a position the device simply could not
 * fix does not. Only the recoverable one gets an action.
 */
export type GeolocationStatus = 'locating' | 'found' | 'denied' | 'unavailable'

export type GeolocationBannerAction = 'none' | 'troubleshoot' | 'maps'

export interface GeolocationBanner {
  mode: BannerMode
  title?: string
  text?: string
  action: GeolocationBannerAction
}

export const DEFAULT_GEOLOCATION_FOUND_TEXT = 'Your location was recorded.'
export const DEFAULT_GEOLOCATION_UNAVAILABLE_TEXT = "Your location couldn't be determined."
export const DEFAULT_GEOLOCATION_PERMISSION_DENIED_TEXT =
  "Location access is turned off, so your location can't be recorded."
export const DEFAULT_OPEN_IN_MAPS_TEXT = 'Open in Maps'
/** Native mobile can reach the OS settings screen; web can only point at instructions. */
export const DEFAULT_OPEN_SETTINGS_TEXT = 'Tap to open settings'
export const DEFAULT_TROUBLESHOOT_TEXT = 'How to fix this'

/**
 * The single decision about what this component puts on screen, shared by all
 * three platforms so they can't drift into saying different things about the
 * same device state.
 *
 * Returns undefined when there is nothing worth saying — which is the default
 * outcome of a working location, and the reason this component is usually
 * invisible.
 */
export function resolveGeolocationBanner(
  status: GeolocationStatus,
  props: UI2GeolocationFieldProps,
  distanceText?: string,
): GeolocationBanner | undefined {
  const {
    isRequired,
    showFoundBanner,
    foundBannerText,
    unavailableWarningText,
    unavailableDangerText,
    permissionDeniedText,
  } = props

  // Acquiring a fix is transient and usually sub-second; announcing it would
  // make every form flicker a banner on mount.
  if (status === 'locating') return undefined

  if (status === 'denied') {
    return {
      mode: isRequired ? 'danger' : 'warning',
      title: permissionDeniedText ?? DEFAULT_GEOLOCATION_PERMISSION_DENIED_TEXT,
      action: 'troubleshoot',
    }
  }

  if (status === 'unavailable') {
    const configured = isRequired ? unavailableDangerText : unavailableWarningText
    return {
      mode: isRequired ? 'danger' : 'warning',
      title: configured ?? DEFAULT_GEOLOCATION_UNAVAILABLE_TEXT,
      action: 'none',
    }
  }

  const title = showFoundBanner ? foundBannerText ?? DEFAULT_GEOLOCATION_FOUND_TEXT : undefined
  const text = distanceText ? `${distanceText} from destination` : undefined
  if (!title && !text) return undefined
  return { mode: 'info', title, text, action: distanceText ? 'maps' : 'none' }
}
